import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useFocusStore } from '@/hooks/use-focus-store'
import {
  useCVDocuments,
  useRetryCVParse,
  useUploadCV,
  type CVDocument,
} from '@/hooks/use-cv-upload'
import { getPrimaryCvDocument } from '@/lib/cv-display'

const OWN_CV_ACCEPT =
  '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
const OWN_CV_MAX_BYTES = 10 * 1024 * 1024

export function useOwnCvReplace() {
  const { focus } = useFocusStore()
  const isCandidate = focus?.current_focus === 'candidate'
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const previousStatusRef = useRef<CVDocument['parsing_status'] | null>(null)
  const { uploadCV, uploading } = useUploadCV()
  const { retryCVParse, isRetrying } = useRetryCVParse()
  const { cvDocuments } = useCVDocuments({ enabled: isCandidate })
  const primary = getPrimaryCvDocument(cvDocuments)

  const busy = uploading || isRetrying
  const isProcessing =
    primary?.parsing_status === 'pending' || primary?.parsing_status === 'processing'
  const isFailed = primary?.parsing_status === 'failed'

  useEffect(() => {
    const status = primary?.parsing_status
    const previous = previousStatusRef.current
    previousStatusRef.current = status ?? null
    if (
      (previous === 'pending' || previous === 'processing') &&
      status === 'completed'
    ) {
      void queryClient.invalidateQueries({ queryKey: ['profile'] })
      void queryClient.invalidateQueries({ queryKey: ['files'] })
    }
  }, [primary?.id, primary?.parsing_status, queryClient])

  const pickFile = () => {
    setError(null)
    inputRef.current?.click()
  }

  const handleFiles = async (fileList: FileList | null) => {
    const file = fileList?.[0]
    if (inputRef.current) inputRef.current.value = ''
    if (!file) return
    if (file.size > OWN_CV_MAX_BYTES) {
      setError('File is too large. Maximum size is 10 MB.')
      return
    }
    setError(null)
    try {
      const result = await uploadCV(file)
      if (!result?.success) {
        setError(result?.error || 'Failed to upload CV')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload CV')
    }
  }

  const handleRetry = async () => {
    if (!primary?.id) return
    setError(null)
    try {
      await retryCVParse(primary.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retry')
    }
  }

  return {
    isCandidate,
    inputRef,
    accept: OWN_CV_ACCEPT,
    handleFiles,
    pickFile,
    handleRetry,
    error,
    busy,
    uploading,
    isRetrying,
    isProcessing,
    isFailed,
    primary,
  }
}
